/**
 * Single source of truth for Aurora retailer search jurisdictions.
 * To add a jurisdiction, append one object to this array.
 *
 * @typedef {Object} AuroraJurisdiction
 * @property {string} label
 *   Display name shown in the jurisdiction dropdown (e.g. "South Dakota").
 *
 * @property {string} site
 *   Host API `site` query param. Must match the backend value exactly,
 *   usually lowercase kebab-case (e.g. "south-dakota", "texas").
 *   Used for retailerInfo, terminalInfo, terminal actions, and repInfo.
 *
 * @property {string} skillKey
 *   Column name on the `retailer_skills` view for skilling-sheet access
 *   (e.g. "ne", "sd", "tx"). Used as the fallback when `csSkillKey` is not set.
 *   Dropdown option is enabled when the user's value for this field is `1`.
 *
 * @property {string} [csSkillKey]
 *   Column name on the `cs_skills` (ES/Aurora) skilling sheet
 *   (e.g. "colorado", "kentucky"). When set, this sheet is checked first
 *   and retailer_skills is not used for that jurisdiction.
 *
 * @property {boolean} [disableTerminalResets]
 *   When true, Soft Reset and Hard Reset are greyed out in the terminal
 *   modal for this site. Other terminal actions are unaffected.
 *
 * @property {boolean} [clearHardwareEnabled]
 *   When true, Clear Hardware ID is available in the terminal modal for
 *   this site. When omitted/false, that action is greyed out.
 *
 * @property {boolean} [disableThresholdExceeded]
 *   When true, Clear Threshold Exceeded is greyed out in the terminal modal.
 *
 * @property {boolean} [disableViewPassword]
 *   When true, View Passwords is greyed out on the retailer search page.

 * @example
 * {
 *   label: 'Minnesota',
 *   site: 'minnesota',
 *   skillKey: 'mn',
 *   disableTerminalResets: false,
 *   clearHardwareEnabled: false,
 * }
 */
export const auroraJurisdictions = [
  {
    label: 'Antilles',
    site: 'antilles',
    skillKey: 'ant',
  },
  {
    label: 'California',
    site: 'california',
    skillKey: 'ca',
    csSkillKey: 'california'
  },
  {
    label: 'Colorado',
    site: 'colorado',
    skillKey: 'co',
    csSkillKey: 'colorado',
    clearHardwareEnabled: true,
  },
  {
    label: 'Connecticut',
    site: 'connecticut',
    skillKey: 'ct',
    disableThresholdExceeded: true,
  },
  {
    label: 'Georgia',
    site: 'georgia',
    skillKey: 'ga',
    clearHardwareEnabled: true,
  },
  {
    label: 'Kentucky',
    site: 'kentucky',
    skillKey: 'ky',
    csSkillKey: 'kentucky',
    disableThresholdExceeded: true,
    clearHardwareEnabled: true,
  },
  {
    label: 'Mississippi',
    site: 'mississippi',
    skillKey: 'ms',
    csSkillKey: 'mississippi',
    clearHardwareEnabled: true,
  },
  {
    label: 'Nebraska',
    site: 'nebraska',
    skillKey: 'ne',
  },
  {
    label: 'New Jersey',
    site: 'new-jersey',
    skillKey: 'nj',
  },
  {
    label: 'North Carolina',
    site: 'north-carolina',
    skillKey: 'nc',
    csSkillKey: 'northCarolina',
    clearHardwareEnabled: true,
  },
  {
    label: 'Rhode Island',
    site: 'rhode-island',
    skillKey: 'ri',
    disableViewPassword: true,
  },
  {
    label: 'South Dakota',
    site: 'south-dakota',
    skillKey: 'sd',
  },
  {
    label: 'Texas',
    site: 'texas',
    skillKey: 'tx',
    clearHardwareEnabled: true,
  },
  {
    label: 'West Virginia',
    site: 'west-virginia',
    skillKey: 'wv',
  }
];

export const jurisdictionOptions = auroraJurisdictions.map(jurisdiction => jurisdiction.label);

export const jurisdictionHostSites = Object.fromEntries(auroraJurisdictions.map(jurisdiction => [jurisdiction.label, jurisdiction.site]));

export const jurisdictionSkillKeys = Object.fromEntries(auroraJurisdictions.map(jurisdiction => [jurisdiction.label, jurisdiction.skillKey]));

export function getAuroraJurisdiction(label) {
  if (!label) {
    return null;
  }
  return auroraJurisdictions.find(jurisdiction => jurisdiction.label === label) ?? null;
}

export function getAuroraJurisdictionBySite(site) {
  if (!site) {
    return null;
  }
  return auroraJurisdictions.find(jurisdiction => jurisdiction.site === site) ?? null;
}

export function isTerminalResetDisabledForSite(site) {
  return Boolean(getAuroraJurisdictionBySite(site)?.disableTerminalResets);
}

export function isClearHardwareEnabledForSite(site) {
  return Boolean(getAuroraJurisdictionBySite(site)?.clearHardwareEnabled);
}

export function isThresholdExceededDisabledForSite(site) {
  return Boolean(getAuroraJurisdictionBySite(site)?.disableThresholdExceeded);
}

export function isViewPasswordDisabledForSite(site) {
  return Boolean(getAuroraJurisdictionBySite(site)?.disableViewPassword);
}

/** API `site` param — keep lowercase; display title case separately in the UI. */
export const getHostSiteForJurisdiction = jurisdiction => jurisdictionHostSites[jurisdiction] ?? '';

function labelToKebabSite(label) {
  if (!label || typeof label !== 'string') {
    return '';
  }
  return label.trim().toLowerCase().replace(/\s+/g, '-');
}

/**
 * Resolve the Host API `site` param from a jurisdiction label or config object.
 * Preferred field order: powerfield, jid, site, value, key; then configured label / kebab fallback.
 */
export function getSiteValue(selectedJurisdiction) {
  if (!selectedJurisdiction) {
    return '';
  }

  if (typeof selectedJurisdiction === 'string') {
    const configured = getAuroraJurisdiction(selectedJurisdiction);
    if (configured) {
      return configured.site;
    }
    return labelToKebabSite(selectedJurisdiction);
  }

  const fromObject =
    selectedJurisdiction.powerfield ??
    selectedJurisdiction.jid ??
    selectedJurisdiction.site ??
    selectedJurisdiction.value ??
    selectedJurisdiction.key;

  if (fromObject) {
    return String(fromObject).trim();
  }

  if (selectedJurisdiction.label) {
    return getSiteValue(selectedJurisdiction.label);
  }

  return '';
}
